/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * The sagas for the widget Search Accounts
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.15.0
 * @since 1.3.0
 */

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { API_HOSTNAME, WIDGET_API, WIDGET_SEARCH_ACCOUNTS } from '../../../../../modules/app/Api';
import { HttpStatus } from '../../../../../modules/app/appConstants';
import { makeSelectTranslationCommon, makeSelectTranslationPageDashboard } from '../../../../../modules/internationalization/intSelectors';
import IPageDashboardTranslation from '../../../../../modules/internationalization/types/pages/dashboard/dashboard';
import { setLocalStorageDataAction } from '../../../../../modules/storage/storageActions';
import { makeSelectLocalStorageSelectedAccount } from '../../../../../modules/storage/storageSelectors';
import { ELocalStorageKeys } from '../../../../../modules/storage/storageTypes';
import { IWizardValidationError } from '../../../../../modules/wizard/wizardTypes';
import { requestSaga } from '../../../../../sagas/requestSaga';
import { formQueryParamsString } from '../../../../../sagas/utils/formQueryParamsString';
import { formatBalance } from '../../../../../utils/formatText';
import { makeSelectorWidgetMetaList } from '../widgetSelectors';
import { EWidgetNames, IWidgetMetaAccountInfo, IWidgetMetaItem } from '../widgetTypes';
import { TWidgetSearchAccountsLoadListAction, TWidgetSearchAccountsSetAccountAction } from './types/widgetSearchAccountsActionTypes';
import {
    EWidgetSearchAccountsField,
    EWidgetSearchAccountsValidationCodes,
    IWidgetSearchAccountsItem,
    IWidgetSearchAccountsResponse,
} from './types/widgetSearchAccountsTypes';
import {
    widgetSearchAccountsLoadListFailed,
    widgetSearchAccountsLoadListSuccessful,
    widgetSearchAccountsSetAccount,
} from './widgetSearchAccountsActions';
import {
    WIDGET_SEARCH_ACCOUNTS_INIT,
    WIDGET_SEARCH_ACCOUNTS_LIST_LENGTH,
    WIDGET_SEARCH_ACCOUNTS_LOAD_LIST,
    WIDGET_SEARCH_ACCOUNTS_SET_ACCOUNT,
} from './widgetSearchAccountsConstants';
import { initialWidgetSearchAccountsItem } from './widgetSearchAccountsReducer';
import { makeSelectWidgetSearchAccountsList, makeSelectWidgetSearchAccountsSearchValue } from './widgetSearchAccountsSelectors';

export function* widgetSearchAccountsLoadListSaga(action: TWidgetSearchAccountsLoadListAction): SagaIterator {
    const start: boolean = action.payload?.start ?? false;
    const searchValue: string = yield select(makeSelectWidgetSearchAccountsSearchValue());
    const accountList: IWidgetSearchAccountsItem[] = yield select(makeSelectWidgetSearchAccountsList());
    const { currency } = yield select(makeSelectTranslationCommon());

    try {
        const url = `${API_HOSTNAME}${WIDGET_API}${WIDGET_SEARCH_ACCOUNTS}`;
        const queryString = formQueryParamsString({
            from: start ? 0 : accountList.length,
            term: encodeURIComponent(searchValue),
            size: WIDGET_SEARCH_ACCOUNTS_LIST_LENGTH,
        });
        const data: IWidgetSearchAccountsResponse = yield call(requestSaga, url + queryString, 'GET');
        const formedData: IWidgetSearchAccountsItem[] = data.accounts.map(item => {
            const balance = item[EWidgetSearchAccountsField.balance]
                ? `${formatBalance(item[EWidgetSearchAccountsField.balance])} ${currency}`
                : '';
            const subsCount = formatBalance(item[EWidgetSearchAccountsField.subsCount], 0);
            return {
                ...item,
                [EWidgetSearchAccountsField.balance]: balance,
                [EWidgetSearchAccountsField.subsCount]: subsCount,
            }
        });
        yield put(widgetSearchAccountsLoadListSuccessful({
            data: start ? formedData : [...accountList, ...formedData],
            hasMore: data.accounts.length === WIDGET_SEARCH_ACCOUNTS_LIST_LENGTH,
        }))

    } catch (e: any) {
        const errorCodes: string[] = [];

        if (e.code === HttpStatus.UNPROCESSABLE_ENTITY) {
            errorCodes.push(EWidgetSearchAccountsValidationCodes.SYNTAX_INCORRECT);
        }
        const errorCode = e.response?.error?.code;
        if (errorCode) {
            errorCodes.push(errorCode);
        }
        const validation = e.response?.error?.validation;
        if (validation && validation.length) {
            validation.forEach((errorValidation: IWizardValidationError) => {
                errorCodes.push(errorValidation.code);
            });
        }

        const translation: IPageDashboardTranslation = yield select(makeSelectTranslationPageDashboard());
        if (errorCodes.length) {
            const message = translation.error.codes[errorCodes[0]] || translation.widgets.searchAccounts.failedText;
            yield put(widgetSearchAccountsLoadListFailed({ message }));
        } else {
            yield put(widgetSearchAccountsLoadListFailed({
                message: translation.widgets.searchAccounts.failedText,
            }));
        }
    }
}

export function* widgetSearchAccountsInitSaga(): SagaIterator {
    const accountMeta: IWidgetMetaAccountInfo = (yield select(makeSelectorWidgetMetaList()))
        .find((widget: IWidgetMetaItem) => widget.name === EWidgetNames.SEARCH_ACCOUNTS).baseAccountInfo;
    const accountLocalStorage = yield select(makeSelectLocalStorageSelectedAccount());
    const account: IWidgetSearchAccountsItem = (accountLocalStorage?.id !== accountMeta?.id)
        ? accountLocalStorage : accountMeta;
    yield put(widgetSearchAccountsSetAccount({
        account: { ...initialWidgetSearchAccountsItem, ...account },
    }))
}

export function* widgetSearchAccountsSaveToStorageSaga(action: TWidgetSearchAccountsSetAccountAction): SagaIterator {
    yield put(setLocalStorageDataAction({
        key: ELocalStorageKeys.DASHBOARD_SELECTED_ACCOUNT,
        value: action.payload.account,
    }));
}

export function* widgetSearchAccountsSagas(): SagaIterator {
    yield all([
        takeEvery(WIDGET_SEARCH_ACCOUNTS_LOAD_LIST, widgetSearchAccountsLoadListSaga),
        takeEvery(WIDGET_SEARCH_ACCOUNTS_INIT, widgetSearchAccountsInitSaga),
        takeEvery(WIDGET_SEARCH_ACCOUNTS_SET_ACCOUNT, widgetSearchAccountsSaveToStorageSaga),
    ])
}
